import { getWsUrl } from "./low_level";
import { get_company_api_url, get_recording_details } from ".";
import type { AnalyticsStatus, RecordingDetails } from "./types";

/**
 * Why a subscription ended without a result: `error` is a connection or server problem, `failed`
 * an analysis that ran and failed, the rest the recording's own state.
 */
export type AnalysisErrorReason =
  | "not_analyzed"
  | "not_synced"
  | "recording_too_short"
  | "cancelled"
  | "fetch_failed"
  | "failed"
  | "error";

type AnalysisStatusMsg =
  | { type: "status"; payload: AnalyticsStatus }
  | { type: "error"; payload: string };

export const subscribeToAnalysisResult = (
  recordingId: string,
  onComplete: (data: RecordingDetails) => void,
  onError: (message: string, reason: AnalysisErrorReason) => void,
  onStatus?: (status: AnalyticsStatus) => void,
): (() => void) => {
  const url = getWsUrl(`/ws/analyze/${encodeURIComponent(recordingId)}/`, get_company_api_url());
  const ws = new WebSocket(url);
  let terminal = false;

  ws.onmessage = event => {
    let msg: AnalysisStatusMsg;
    try {
      msg = JSON.parse(event.data);
    } catch {
      terminal = true;
      ws.close();
      onError("Malformed server message", "error");
      return;
    }
    if (msg.type === "error") {
      terminal = true;
      ws.close();
      onError(msg.payload, "error");
      return;
    }
    if (msg.type !== "status") {
      terminal = true;
      ws.close();
      onError("Unexpected server message", "error");
      return;
    }
    const s = msg.payload;
    if (s.status === "analyzing") {
      onStatus?.(s);
    } else if (s.status === "completed") {
      terminal = true;
      ws.close();
      get_recording_details(recordingId)
        .then(onComplete)
        .catch(err => {
          onError(err instanceof Error ? err.message : "Failed to load results", "fetch_failed");
        });
    } else if (s.status === "failed") {
      terminal = true;
      ws.close();
      onError(s.error_message ?? "Analysis failed", "failed");
    } else if (s.status === "not_analyzed") {
      terminal = true;
      ws.close();
      onError("Recording not analyzed", "not_analyzed");
    } else if (s.status === "not_synced") {
      terminal = true;
      ws.close();
      onError("Recording not synced", "not_synced");
    } else if (s.status === "recording_too_short") {
      terminal = true;
      ws.close();
      onError("Recording too short", "recording_too_short");
    } else if (s.status === "cancelled") {
      terminal = true;
      ws.close();
      onError("Analysis cancelled", "cancelled");
    }
  };

  ws.onerror = () => {
    if (terminal) return;
    terminal = true;
    onError("WebSocket connection error", "error");
  };

  ws.onclose = () => {
    if (terminal) return;
    terminal = true;
    onError("Connection closed before analysis completed", "error");
  };

  return () => {
    terminal = true;
    ws.close();
  };
};
